import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, LayoutGroup } from 'framer-motion'
import { Terminal } from 'lucide-react'

const NAV_LINKS = [
  { href: '/api', label: 'API Reference' },
  { href: '/playground', label: 'Playground' },
  { href: '/status', label: 'Status' },
  { href: '/contact', label: 'Contact' },
]

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false)
  const [hoveredHref, setHoveredHref] = useState<string | null>(null)
  const location = useLocation()

  const indicatorTarget = hoveredHref ?? location.pathname

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[rgba(13,17,23,0.82)] backdrop-blur-2xl border-b border-white/[0.06] shadow-[0_4px_30px_rgba(0,0,0,0.4)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
        {/* Brand wordmark */}
        <Link to="/" aria-label="LUKHAS.IO home">
          <span
            className="text-xl text-white tracking-[0.3em] transition-all duration-500"
            style={{
              ...brandFont,
              textShadow: scrolled
                ? '0 0 24px rgba(13,148,136,0.6), 0 0 48px rgba(13,148,136,0.2)'
                : '0 0 20px rgba(13,148,136,0.35)',
            }}
          >
            LUKH&Lambda;S<span className="text-vision-teal">.IO</span>
          </span>
        </Link>

        {/* Nav — sliding underline indicator (same pattern as lukhas.com) */}
        <LayoutGroup id="nav-io">
          <nav
            className="hidden md:flex items-center gap-8 relative"
            onMouseLeave={() => setHoveredHref(null)}
          >
            {NAV_LINKS.map(({ href, label }) => (
              <Link
                key={href}
                to={href}
                className="relative pb-1 text-sm tracking-[0.1em] transition-colors duration-200 text-white/60 hover:text-white data-[active=true]:text-white"
                data-active={location.pathname === href}
                aria-current={location.pathname === href ? 'page' : undefined}
                style={bodyFont}
                onMouseEnter={() => setHoveredHref(href)}
              >
                {label}
                {indicatorTarget === href && (
                  <motion.span
                    layoutId="nav-indicator-io"
                    className="absolute bottom-0 left-0 right-0 h-px bg-vision-teal"
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.1, 1] }}
                    style={{ transformOrigin: 'left center' }}
                  />
                )}
              </Link>
            ))}
          </nav>
        </LayoutGroup>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <a
            href="https://lukhas.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition-colors tracking-[0.08em]"
            style={bodyFont}
          >
            <Terminal className="w-3.5 h-3.5" />
            Docs
          </a>
          <a
            href="https://lukhas.id/login"
            className="px-4 py-1.5 rounded-full text-sm bg-vision-teal/20 border border-vision-teal/40 text-vision-teal hover:bg-vision-teal/30 transition-all tracking-[0.06em]"
            style={bodyFont}
          >
            Get API Key
          </a>
        </div>
      </div>
    </motion.header>
  )
}
